import * as THREE from 'three';

export const SHADOW_CONFIG = {
	enabled: true,
	type: THREE.PCFSoftShadowMap,
	mapSize: 4096,
	autoUpdate: true,

	bias: -0.0001,
	normalBias: 0.1,
	radius: 1,

	cameraMultiplier: 2.5,
	cameraNearMultiplier: 0.1,
	cameraFarMultiplier: 1.5,

	minNear: 1,
	maxShadowDistance: 10000,
} as const;

export const configureShadowRenderer = (renderer: THREE.WebGLRenderer): void => {
	renderer.shadowMap.enabled = SHADOW_CONFIG.enabled;
	renderer.shadowMap.type = SHADOW_CONFIG.type;
	renderer.shadowMap.autoUpdate = SHADOW_CONFIG.autoUpdate;
};

export const configureShadowLight = (
	directionalLight: THREE.DirectionalLight,
	sceneBounds: THREE.Box3,
	lightDistance: number
): void => {
	directionalLight.castShadow = true;

	directionalLight.shadow.mapSize.width = SHADOW_CONFIG.mapSize;
	directionalLight.shadow.mapSize.height = SHADOW_CONFIG.mapSize;

	directionalLight.shadow.bias = SHADOW_CONFIG.bias;
	directionalLight.shadow.normalBias = SHADOW_CONFIG.normalBias;
	directionalLight.shadow.radius = SHADOW_CONFIG.radius;

	const shadowCamera = directionalLight.shadow.camera as THREE.OrthographicCamera;
	const size = new THREE.Vector3();
	const center = new THREE.Vector3();
	sceneBounds.getSize(size);
	sceneBounds.getCenter(center);

	const maxDimension = Math.max(size.x, size.y, size.z);
	const shadowSize = maxDimension * SHADOW_CONFIG.cameraMultiplier;

	const groundMargin = Math.max(shadowSize * 0.1, 50);

	const finalShadowSize = shadowSize + groundMargin;
	shadowCamera.left = -finalShadowSize;
	shadowCamera.right = finalShadowSize;
	shadowCamera.top = finalShadowSize;
	shadowCamera.bottom = -finalShadowSize;

	const groundLevel = sceneBounds.min.z;
	const lightToGroundDistance = Math.abs(groundLevel) + lightDistance;

	const optimalNear = Math.max(
		SHADOW_CONFIG.minNear,
		lightDistance * SHADOW_CONFIG.cameraNearMultiplier
	);
	const optimalFar = Math.max(
		lightToGroundDistance * 1.2,
		Math.min(lightDistance * SHADOW_CONFIG.cameraFarMultiplier, SHADOW_CONFIG.maxShadowDistance)
	);

	shadowCamera.near = optimalNear;
	shadowCamera.far = optimalFar;

	shadowCamera.updateProjectionMatrix();
	directionalLight.shadow.needsUpdate = true;

	const lightWithPrecision = directionalLight as THREE.DirectionalLight & {
		shadowPrecision?: {
			mapSize: number;
			nearFarRatio: number;
			shadowTexelSize: number;
		};
	};
	lightWithPrecision.shadowPrecision = {
		mapSize: SHADOW_CONFIG.mapSize,
		nearFarRatio: optimalFar / optimalNear,
		shadowTexelSize: (shadowSize * 2) / SHADOW_CONFIG.mapSize,
	};

	if (directionalLight.shadow.map) {
		directionalLight.shadow.map.dispose();
		directionalLight.shadow.map = null;
	}
};
